import Box from "@mui/material/Box";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridApiPro,
  GridColDef,
  GridFilterItem,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import { MutableRefObject, ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuditRow } from "../../models/AuditRow";
import { AuditTrip } from "../../models/AuditTrip";
import { AuditView } from "../../models/AuditView";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { VIEW_TONES } from "../header/ViewSwitch";
import { AuditNoRowsOverlay, EmptyReason } from "./AuditNoRowsOverlay";
import { VehicleGroupingCell } from "./cells/VehicleGroupingCell";
import { LoadMoreBar, LoadMoreProps } from "./LoadMoreBar";
import { defaultHiddenFields } from "./useAuditColumns";
import { I18nGrid } from "@/components/data/I18nGrid";

interface Props {
  apiRef: MutableRefObject<GridApiPro>;
  rows: AuditRow[];
  columns: GridColDef<AuditRow>[];
  view: AuditView;
  loading: boolean;
  emptyReason: EmptyReason;
  storedState: GridInitialStatePro | null;
  onStateChange: (state: GridInitialStatePro) => void;
  onExpansionChange: (expandedDayIds: Set<GridRowId>) => void;
  onShowDetail: (trip: AuditTrip) => void;
  /** The line under the rows: status, entry count and the button that loads more vehicles. */
  loadMore: LoadMoreProps;
  /** Shows the filter row under the column headers; the filters stay in effect while hidden. */
  showHeaderFilters: boolean;
  /** Reports how many column filters are in effect, so a hidden filter row can still say so. */
  onFilterCountChange: (count: number) => void;
  /** Checkbox selection for assigning driver and reason; omitted for read-only users and the analog view. */
  selection?: GridSelection;
  /** Rendered between the bar above and the grid, e.g. the assignment bar. */
  topBar?: ReactNode;
}

export interface GridSelection {
  model: GridRowSelectionModel;
  onChange: (model: GridRowSelectionModel) => void;
  isRowSelectable: (row: AuditRow) => boolean;
}

/** The checkbox column sits pinned in front of the vehicle column while selection is on; stored layouts never contain it. */
function pinnedLeft(stored: string[] | undefined, selectable: boolean): string[] {
  const left = stored ?? [GRID_TREE_DATA_GROUPING_FIELD];
  return selectable ? [GRID_CHECKBOX_SELECTION_COL_DEF.field, ...left] : left;
}

/** Widths the user dragged, from the stored layout. */
function storedWidths(storedState: GridInitialStatePro | null): Record<string, number> {
  const dimensions = storedState?.columns?.dimensions ?? {};
  const widths: Record<string, number> = {};
  for (const [field, dimension] of Object.entries(dimensions)) {
    if (typeof dimension?.width === "number" && dimension.width > 0) widths[field] = dimension.width;
  }
  return widths;
}

const GROUPING_COLUMN_WIDTH = 300;

/** Operators like "is empty" filter without a value; every other item needs one to take effect. */
function isActiveFilter(item: GridFilterItem): boolean {
  if (item.operator === "isEmpty" || item.operator === "isNotEmpty") return true;
  return Array.isArray(item.value) ? item.value.length > 0 : item.value !== undefined && item.value !== "";
}

/**
 * Tree-data grid: one day row per vehicle and day, trip rows underneath. Column state is
 * restored from the user's stored settings and saved back on every change. The grid scrolls
 * its rows itself, so the column headers and the filter row (when shown) stay put. Dragged widths are
 * written back into the column definitions: the definitions change identity whenever
 * addresses or driver names arrive, and the grid would otherwise fall back to their widths.
 */
export function AuditGrid({
  apiRef,
  rows,
  columns,
  view,
  loading,
  emptyReason,
  storedState,
  onStateChange,
  onExpansionChange,
  onShowDetail,
  loadMore,
  showHeaderFilters,
  onFilterCountChange,
  selection,
  topBar,
}: Props) {
  const { t } = useTranslation();
  const [widths, setWidths] = useState<Record<string, number>>(() => storedWidths(storedState));
  const selectable = !!selection;

  const sizedColumns = useMemo(
    () =>
      columns.map(column =>
        widths[column.field] != null ? { ...column, width: widths[column.field], flex: undefined } : column,
      ),
    [columns, widths],
  );

  const initialState = useMemo<GridInitialStatePro>(() => {
    const left = pinnedLeft(storedState?.pinnedColumns?.left, selectable);
    const defaults: GridInitialStatePro = {
      columns: {
        columnVisibilityModel: Object.fromEntries(defaultHiddenFields(view).map(field => [field, false])),
      },
      // Vehicles alphabetical, their days oldest first, a day's trips chronological.
      sorting: {
        sortModel: [
          { field: GRID_TREE_DATA_GROUPING_FIELD, sort: "asc" },
          { field: "date", sort: "asc" },
          { field: "firstStart", sort: "asc" },
        ],
      },
      pinnedColumns: { left },
    };
    if (!storedState) return defaults;
    return {
      ...defaults,
      ...storedState,
      // Only column layout is persisted; the sort order always starts from the default.
      sorting: defaults.sorting,
      columns: {
        ...storedState.columns,
        columnVisibilityModel: {
          ...defaults.columns?.columnVisibilityModel,
          ...storedState.columns?.columnVisibilityModel,
        },
      },
      pinnedColumns: { ...storedState.pinnedColumns, left },
    };
  }, [storedState, view, selectable]);

  useEffect(() => {
    const expanded = new Set<GridRowId>();
    return apiRef.current.subscribeEvent("rowExpansionChange", node => {
      if (node.childrenExpanded) expanded.add(node.id);
      else expanded.delete(node.id);
      onExpansionChange(new Set(expanded));
    });
  }, [apiRef, onExpansionChange]);

  const groupingColDef = useMemo<GridGroupingColDefOverride<AuditRow>>(
    () => ({
      headerName: t("logbookAudit.columns.device"),
      // A fixed width, not flex: the pinned header keeps the width of the very first render
      // (0 px before the grid has measured itself) when the grid mounts with data already loaded.
      width: widths[GRID_TREE_DATA_GROUPING_FIELD] ?? GROUPING_COLUMN_WIDTH,
      hideDescendantCount: true,
      sortable: true,
      valueGetter: (_value, row) => row.device.name,
      renderCell: (params: GridRenderCellParams<AuditRow>) => (
        <VehicleGroupingCell {...params} onShowDetail={onShowDetail} />
      ),
    }),
    [t, onShowDetail, widths],
  );

  const save = () => onStateChange(apiRef.current.exportState());
  const saveWidth = (field: string, width: number) => {
    setWidths(current => ({ ...current, [field]: width }));
    save();
  };

  return (
    <Box
      sx={{
        background: AUDIT_TOKENS.surface,
        border: `1px solid ${AUDIT_TOKENS.line}`,
        borderRadius: `${AUDIT_TOKENS.radius.card}px`,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {topBar}
      <I18nGrid
        apiRef={apiRef}
        treeData
        rows={rows}
        columns={sizedColumns}
        getRowId={row => row.id}
        getTreeDataPath={row => row.path}
        groupingColDef={groupingColDef}
        initialState={initialState}
        headerFilters={showHeaderFilters}
        hideFooter
        loading={loading}
        rowHeight={36}
        columnHeaderHeight={38}
        headerFilterHeight={40}
        onFilterModelChange={model => onFilterCountChange(model.items.filter(isActiveFilter).length)}
        disableRowSelectionOnClick
        checkboxSelection={selectable}
        rowSelection={selectable}
        rowSelectionModel={selection?.model}
        onRowSelectionModelChange={selection?.onChange}
        isRowSelectable={(params: GridRowParams<AuditRow>) => selection?.isRowSelectable(params.row) ?? false}
        rowSelectionPropagation={{ parents: true, descendants: true }}
        disableColumnFilter={false}
        getRowClassName={params => (params.row.rowType === "day" ? "audit-day" : "audit-trip")}
        onColumnVisibilityModelChange={save}
        onColumnOrderChange={save}
        onColumnWidthChange={params => saveWidth(params.colDef.field, params.width)}
        onPinnedColumnsChange={save}
        slots={{ noRowsOverlay: AuditNoRowsOverlay }}
        slotProps={{
          noRowsOverlay: { emptyReason },
          loadingOverlay: { variant: "linear-progress", noRowsVariant: rows.length ? "linear-progress" : "skeleton" },
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          border: 0,
          fontSize: 13.5,
          fontFamily: AUDIT_TOKENS.fontFamily,
          color: AUDIT_TOKENS.text,
          "--DataGrid-containerBackground": AUDIT_TOKENS.ink,
          "--DataGrid-pinnedBackground": AUDIT_TOKENS.surface,
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
            background: AUDIT_TOKENS.ink,
            color: "#fff",
          },
          "& .MuiDataGrid-columnHeaderTitle": { fontSize: 12, fontWeight: 650, letterSpacing: ".02em" },
          "& .MuiDataGrid-columnHeader .MuiIconButton-root, & .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
            color: "rgba(255,255,255,.75)",
          },
          "& .MuiDataGrid-headerFilterRow .MuiInputBase-root": {
            fontSize: 11.5,
            color: "#fff",
            background: "rgba(255,255,255,.12)",
            borderRadius: `${AUDIT_TOKENS.radius.small}px`,
          },
          "& .MuiDataGrid-headerFilterRow .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,.7)", opacity: 1 },
          "& .MuiDataGrid-headerFilterRow .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.25)" },
          "& .MuiDataGrid-headerFilterRow .MuiInputLabel-root": { color: "rgba(255,255,255,.7)", fontSize: 11.5 },
          "& .MuiDataGrid-cell": {
            borderTop: `1px solid ${AUDIT_TOKENS.line}`,
            borderBottom: 0,
            padding: "0 12px",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered": { background: AUDIT_TOKENS.rowHover },
          "& .MuiDataGrid-row.audit-day": { boxShadow: `inset 4px 0 0 ${VIEW_TONES[view].fg}` },
          "& .MuiDataGrid-row.audit-trip": { background: "#FCFDFE" },
          "& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible": {
            outline: AUDIT_TOKENS.focusRing,
            outlineOffset: -2,
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { outline: "none" },
          "@media (prefers-reduced-motion: reduce)": { "& *": { transition: "none !important" } },
        }}
      />
      <LoadMoreBar {...loadMore} />
    </Box>
  );
}
